import { useState, ChangeEvent, useMemo } from 'react';

import { ValueLabel } from '@/types';

import { useAppDispatch, useAppSelector } from '@/redux';
import { formatDate } from '@/utils/dateUtils';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { CartModel } from '@/models';
import { selectBlockingMotives } from '@/redux/slices/checkout.slice';

import CheckboxV2 from '@/components/common/CheckboxV2';
import SelectDropdown from '@/components/common/SelectDropdown';

import styles from './AdminFields.module.scss';
import TextFieldV2 from '@/components/common/form/fields/TextFieldV2';
import Form from '@/components/common/form/Form';
import { useDebounce } from '@/hooks/useDebounce';
import { updateExproOrder } from '@/redux/slices/expro.slice';

const adminZonePrefix = 'admin:checkout.adminZone';
const BLOCK_SHIPPING_FOR_WEATHER = 5;
const DELAY_SHIPPING_FOR_NON_ATTENDANCE = 2;
const STORAGE_SHIP_MODE = 'FRStock';

type Props = {
  t: (key: string) => string;
  cart: CartModel;
  checkoutType?: 'primeur' | 'classic';
}
const AdminFields = ({
  t, cart, checkoutType = 'classic'
}: Props) => {
  const dispatch = useAppDispatch();
  const { blockShippingDate, blockShippingReason, shippingMode } = cart;
  const { cartStorage } = useAppSelector(({ storage }) => storage);
  const exproOrder = useAppSelector(({ expro }) => expro.exproOrder);
  const blockingMotives = useAppSelector(selectBlockingMotives);
  const [showBlockShipping, setShowBlockShipping] = useState(!!(blockShippingDate || blockShippingReason));
  const storageAnniversaryDate = useAppSelector(({ user }) => user.content?.storageAnniversaryDate);

  const isWeatherHoldChecked = shippingMode?.canBeBlocked && shippingMode?.weatherHoldChecked;
  const isDelayedShippingChecked = shippingMode?.canBeDelayed && shippingMode?.selectedDeliveryDateDelayed?.length > 0;

  const cellarsComment = cart.cellarsComment;
  const cartCommentWithWeatherHoldChecked = isWeatherHoldChecked && t(`${adminZonePrefix}.inputs.weatherHoldBlockedShippingReason`);
  const cartCommentWithDelayedShippingChecked = isDelayedShippingChecked && t(`${adminZonePrefix}.inputs.delayedShipping`);

  const [cartComments, setCartComments] = useState(
    cellarsComment || cartCommentWithWeatherHoldChecked || cartCommentWithDelayedShippingChecked || ''
  );

  const handlerFunctions = useMemo(() => ({
    primeur: {
      handleBlockShippingChange: () => {
        return;
      },
      handleBlockShippingDateChange: (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateExproOrder({ ...exproOrder, blockShippingDate: e.target.value }));
      },
      handleBlockShippingReasonChange: (value: string) => {
        dispatch(updateExproOrder({ ...exproOrder, blockShippingReason: value }));
      },
      handleUrgentStatus: (isUrgent: boolean) => {
        dispatch(updateCartAttributeByName({ attributeName: 'isUrgent', newValue: isUrgent }));
        dispatch(updateExproOrder({ ...exproOrder, isUrgent: isUrgent }));
      },
      handleCellarsCommentUpdate: (value: string) => {
        dispatch(updateExproOrder({ ...exproOrder, cellarsComment: value }));
      },
      handleLocalCellarsCommentUpdate: (localCellarsComment: string) => {
        setCartComments(localCellarsComment);
      },
      handleStorageOfferedStatus: () => {
        return;
      }
    },
    classic: {
      handleBlockShippingChange: (isShippingBlocked: boolean) => {
        setShowBlockShipping(isShippingBlocked);
        if (isShippingBlocked && checkoutType === 'classic') {
          dispatch(updateCartAttributeByName({ attributeName: 'isUrgent', newValue: false }));
        }

        if (!isShippingBlocked) {
          dispatch(updateCartAttributeByName({ attributeName: 'blockShippingDate', newValue: null }));
          dispatch(updateCartAttributeByName({ attributeName: 'blockShippingReason', newValue: null }));
        }
      },
      handleBlockShippingDateChange: (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateCartAttributeByName({ attributeName: 'blockShippingDate', newValue: e.target.value }));
      },
      handleBlockShippingReasonChange: (value: string) => {
        dispatch(updateCartAttributeByName({ attributeName: 'blockShippingReason', newValue: value }));
      },
      handleUrgentStatus: (isUrgent: boolean) => {
        if (isUrgent) {
          handlerFunctions.classic.handleBlockShippingChange(false);
        }
        dispatch(updateCartAttributeByName({ attributeName: 'isUrgent', newValue: isUrgent }));
      },
      handleCellarsCommentUpdate: (value: string) => {
        dispatch(updateCartAttributeByName({ attributeName: 'cellarsComment', newValue: value }));
      },
      handleLocalCellarsCommentUpdate: (localCellarsComment: string) => {
        setCartComments(localCellarsComment);
      },
      handleStorageOfferedStatus: (isStorageOffered: boolean) => {
        dispatch(updateCartAttributeByName({ attributeName: 'isStorageOffered', newValue: isStorageOffered }));
      }
    }
  }), [checkoutType]);

  useDebounce(cartComments, 500, (debouncedValue: string) => {
    handlerFunctions[checkoutType].handleCellarsCommentUpdate(debouncedValue);
  });

  const assignWeatherHoldToSelectedValue = isWeatherHoldChecked && { value: BLOCK_SHIPPING_FOR_WEATHER };
  const assignDelayedShippingToSelectedValue = isDelayedShippingChecked && { value: DELAY_SHIPPING_FOR_NON_ATTENDANCE };
  const assignBlockShippingReasonToSelectedValue = blockShippingReason && { value: blockShippingReason };

  const selectedValueForBlockedShipping = assignWeatherHoldToSelectedValue || assignDelayedShippingToSelectedValue ||
    assignBlockShippingReasonToSelectedValue || null;

  const isCartToStoreInCellar = cart.shippingMode?._id === STORAGE_SHIP_MODE;

  return (
    <div className={styles.container} data-test-id="admin-fields">
      <Form>
        <div className={styles.form}>
          <h2>{t(`${adminZonePrefix}.title`)}</h2>
          <div>
            {cartStorage.length > 0 && storageAnniversaryDate && (
              <div
                data-test-id="storage-anniversary-date">{`${t('forms.storageAnniversaryDate')} : ${formatDate(storageAnniversaryDate)}`}</div>
            )}

            <label htmlFor="cellars-comment">{t(`${adminZonePrefix}.inputs.cellarsComment`)}</label>
            <TextFieldV2
              id="cellars-comment"
              name="cellars-comment"
              label={t(`${adminZonePrefix}.inputs.cellarsComment`)}
              data-test-id="cellars-comment"
              value={cartComments}
              onChange={(e) => handlerFunctions[checkoutType].handleLocalCellarsCommentUpdate(e.target.value.trim())}
              disabled={isWeatherHoldChecked || isDelayedShippingChecked}
            />
          </div>

          <CheckboxV2
            id="is-urgent"
            data-test-id="is-urgent"
            rbf="is-urgent"
            checked={cart.isUrgent}
            onChange={(e) => handlerFunctions[checkoutType].handleUrgentStatus(e.target.checked)}
            label={t(`${adminZonePrefix}.inputs.isUrgent`)}
          />

          <CheckboxV2
            id="show-block-shipping"
            data-test-id="show-block-shipping"
            rbf="show-block-shipping"
            checked={showBlockShipping}
            onChange={(e) => handlerFunctions[checkoutType].handleBlockShippingChange(e.target.checked)}
            label={t(`${adminZonePrefix}.inputs.showBlockShipping`)}
          />

          {showBlockShipping && (
            <>
              <div>
                <label htmlFor="block-shipping-date">{t(`${adminZonePrefix}.inputs.blockShippingDate`)}</label>
                <input
                  id="block-shipping-date"
                  data-test-id="block-shipping-date"
                  onChange={(e) => handlerFunctions[checkoutType].handleBlockShippingDateChange(e)}
                  defaultValue={
                    isDelayedShippingChecked ? formatDate(shippingMode.selectedDeliveryDateDelayed) : formatDate(blockShippingDate)
                  }
                  type="date"
                  disabled={isWeatherHoldChecked || isDelayedShippingChecked}
                />
              </div>
              <div>
                <label htmlFor="block-shipping-reason">{t(`${adminZonePrefix}.inputs.blockShippingReason`)}</label>
                <SelectDropdown
                  id="block-shipping-reason"
                  data-test-id="block-shipping-reason"
                  options={blockingMotives}
                  onChange={(e: ValueLabel) => handlerFunctions[checkoutType].handleBlockShippingReasonChange(e.value)}
                  selected={selectedValueForBlockedShipping}
                  disabled={isWeatherHoldChecked || isDelayedShippingChecked || !blockingMotives?.length}
                />
              </div>
            </>
          )}

          {isCartToStoreInCellar && (
            <CheckboxV2
              id="offer-storage"
              checked={cart.isStorageOffered}
              label={t(`${adminZonePrefix}.inputs.offerStorage`)}
              onChange={(e) => handlerFunctions[checkoutType].handleStorageOfferedStatus(e.target.checked)}
            />
          )}
        </div>
      </Form>
    </div>
  )
  ;
};

export default AdminFields;
